import 'core-js/stable';
import 'isomorphic-fetch';
import 'raf/polyfill';
import 'regenerator-runtime/runtime';

import * as Sentry from '@sentry/browser';
import { StrictMode } from 'react';
import { hydrate } from 'react-dom';
import ReactGA from 'react-ga';
import { BrowserRouter } from 'react-router-dom';

import { RelayEnvironmentProvider } from 'react-relay';

import App from './App';
import shouldUseSentry from './router/utils/shouldUseSentry';

import authenticationRoutes from './router/routes/authenticationRoutes';
import dashboardRoutes from './router/routes/dashboardRoutes';
import publicRoutes from './router/routes/publicRoutes';
import requiredRoutes from './router/routes/requiredRoutes';

import { config } from './config';
import { appendRoutes } from './router/routes';
import { initEnvironment } from './relay';
import HiringProviders from './HiringProviders';

appendRoutes(authenticationRoutes);
appendRoutes(dashboardRoutes);
appendRoutes(requiredRoutes);
appendRoutes(publicRoutes);

// setup sentry
if (shouldUseSentry) {
  Sentry.init({
    dsn: config.SENTRY_DSN,
    release: config.SENTRY_RELEASE,
    environment: config.FEEDBACK_ENV,
  });

  Sentry.configureScope((scope) => {
    scope.setTag('git_commit', config.COMMIT_SHA);
  });
}

ReactGA.initialize(config.GA_ID);

const runApp = () => {
  const rootEl = document.getElementById('root');

  const getBasename = () => {
    // eslint-disable-next-line
    const [_, basename] = location.pathname.split('/');

    if (basename) {
      return `/${basename}`;
    }

    return '/hiring';
  };

  const environment = initEnvironment();

  hydrate(
    <StrictMode>
      <RelayEnvironmentProvider environment={environment}>
        <HiringProviders>
          <BrowserRouter basename={getBasename()}>
            <App />
          </BrowserRouter>
        </HiringProviders>
      </RelayEnvironmentProvider>
    </StrictMode>,
    rootEl,
  );
};

// Intl polyfill
if (!global.Intl) {
  (async () => {
    try {
      await import('intl'); // eslint-disable-line no-undef

      await import('intl/locale-data/jsonp/pt-BR.js');
      import('intl/locale-data/jsonp/en.js');
    } catch (err) {
      // eslint-disable-next-line
      console.log('error loading intl polyfill: ', err);
    }

    runApp();
  })();
} else {
  runApp();
}
